import { useState, useCallback } from 'react';
import { useSearch, PAGE_SIZE, type SourceFilter } from '../hooks/useSearch.js';
import type { SearchHit } from '../hooks/useSearch.js';

interface Props {
  onClose: () => void;
}

const SOURCE_LABELS: Record<string, string> = {
  all: 'Alle',
  twitch: 'Twitch',
  youtube: 'YouTube',
};

export default function SearchPage({ onClose }: Props) {
  const [q, setQ] = useState('');
  const [source, setSource] = useState<SourceFilter>('all');
  const [offset, setOffset] = useState(0);
  const { result, loading, error, search } = useSearch();

  const doSearch = useCallback(
    (newQ: string, newSource: SourceFilter, newOffset: number) => {
      void search(newQ, newSource, newOffset);
    },
    [search],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setOffset(0);
    doSearch(q, source, 0);
  };

  const handleSourceChange = (s: SourceFilter) => {
    setSource(s);
    setOffset(0);
    if (q.trim()) doSearch(q, s, 0);
  };

  const handlePage = (newOffset: number) => {
    setOffset(newOffset);
    doSearch(q, source, newOffset);
  };

  const totalPages = result ? Math.ceil(result.total / PAGE_SIZE) : 0;
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      <header className="flex items-center gap-3 px-4 py-3 border-b border-slate-800 flex-shrink-0">
        <button
          type="button"
          onClick={onClose}
          className="text-slate-400 hover:text-slate-200 text-sm underline"
        >
          ← Zurück
        </button>
        <h1 className="text-base font-semibold">Chat-Suche</h1>
      </header>

      <div className="px-4 py-3 border-b border-slate-800 flex-shrink-0">
        <form onSubmit={handleSubmit} className="flex gap-2">
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Suche nach Nachrichten, Absendern, @Erwähnungen…"
            className="flex-1 bg-slate-800 border border-slate-700 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-purple-500 placeholder:text-slate-500"
            autoFocus
            maxLength={500}
          />
          <button
            type="submit"
            disabled={loading || !q.trim()}
            className="bg-purple-600 hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-1.5 rounded transition-colors"
          >
            Suchen
          </button>
        </form>

        <div className="flex gap-1 mt-2">
          {(['all', 'twitch', 'youtube'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => handleSourceChange(s)}
              className={`text-xs px-2.5 py-1 rounded transition-colors ${
                source === s
                  ? 'bg-purple-700 text-white'
                  : 'bg-slate-800 text-slate-400 hover:text-slate-200'
              }`}
            >
              {SOURCE_LABELS[s]}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
        {loading && <p className="text-slate-400 text-sm text-center py-8">Suche…</p>}
        {error && <p className="text-red-400 text-sm text-center py-8">{error}</p>}
        {!loading && !error && result && result.hits.length === 0 && (
          <p className="text-slate-500 text-sm text-center py-8">
            Keine Ergebnisse für „{result.query}"
          </p>
        )}
        {!loading && result && result.hits.length > 0 && (
          <>
            <p className="text-xs text-slate-500 mb-2">
              {result.total} Treffer für „{result.query}"
              {source !== 'all' ? ` in ${SOURCE_LABELS[source]}` : ''}
            </p>
            {result.hits.map((hit) => (
              <SearchHitCard key={hit.id} hit={hit} query={result.query} />
            ))}
          </>
        )}
      </div>

      {result && result.total > PAGE_SIZE && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800 flex-shrink-0">
          <button
            type="button"
            onClick={() => handlePage(offset - PAGE_SIZE)}
            disabled={offset === 0}
            className="text-sm text-slate-400 hover:text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ← Zurück
          </button>
          <span className="text-xs text-slate-500">
            Seite {currentPage} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => handlePage(offset + PAGE_SIZE)}
            disabled={offset + PAGE_SIZE >= result.total}
            className="text-sm text-slate-400 hover:text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Weiter →
          </button>
        </div>
      )}
    </div>
  );
}

function SearchHitCard({ hit, query }: { hit: SearchHit; query: string }) {
  const date = new Date(hit.createdAt);
  const dateStr = date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className="bg-slate-900 border border-slate-800 rounded px-3 py-2 text-sm">
      <div className="flex items-baseline gap-2 mb-1">
        <span className="font-medium text-purple-300">
          <HighlightedText text={hit.username} query={query} />
        </span>
        <span className="text-xs text-slate-500">{hit.source}</span>
        <span className="text-xs text-slate-600 ml-auto">{dateStr}</span>
      </div>
      <p className="text-slate-300 text-xs leading-relaxed">
        <HighlightedText text={hit.content} query={query} />
      </p>
    </div>
  );
}

function HighlightedText({ text, query }: { text: string; query: string }) {
  // Extract individual words from the query, ignoring FTS operators
  const words = query
    .replace(/[+\-|"()]/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 1);

  if (words.length === 0) return <>{text}</>;

  const pattern = new RegExp(`(${words.map(escapeRegex).join('|')})`, 'gi');
  const parts = text.split(pattern);

  return (
    <>
      {parts.map((part, i) =>
        pattern.test(part) ? (
          <mark key={i} className="bg-yellow-400/30 text-yellow-200 rounded-sm px-0.5">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
